import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import { Carte } from '../../../utils/types/Carte';
import CarteDetails from '../../modals/details/CarteDetails';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);

interface CarteGridProps {
    cartes: Carte[];
    codeClient: string;
    handleCarteUpdated?: (carte: Carte) => void;
    auditView?: boolean;
}

const CarteGrid: React.FC<CarteGridProps> = ({ cartes, codeClient, handleCarteUpdated, auditView }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [columnDefs, setColumnDefs] = useState<ColDef<Carte>[]>([
        {
            field: 'numero',
            headerName: 'Numéro',
            cellRenderer: (params: any) => <CarteDetails pCarte={params.data} codeClient={codeClient} onUpdate={handleCarteUpdated} />,
            pinned: isMobile ? undefined : 'left',
            sortable: true,
            filter: true,
        },
        { field: 'type', headerName: 'Type', sortable: true, filter: true },
        { field: 'description', headerName: 'Porteur', sortable: true, filter: true },
        { field: 'designationArticle', headerName: 'Article', sortable: true, filter: true },

        {
            field: 'solde',
            headerName: 'Solde',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => {
                const value = params.value;
                if (value == null) return '';
                if (params.data?.idArticle == null) {
                    return new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(value);
                } else {
                    return `${value.toFixed(2).replace('.', ',')} L`;
                }
            },
            cellStyle: { textAlign: 'right' },
        },

        { field: 'nomBranche', headerName: 'Zone géographique', sortable: true, filter: true },
        {
            field: 'dateValidite',
            headerName: 'Date validité',
            sortable: true,
            valueFormatter: (params) => (params.value ? format(params.value, 'dd/MM/yyyy') : ''),
            filter: 'agDateColumnFilter',
        },
        {
            field: 'dateExtremeValidite',
            headerName: 'Date extrême validité',
            sortable: true,
            valueFormatter: (params) => (params.value ? format(params.value, 'dd/MM/yyyy') : ''),
            filter: 'agDateColumnFilter',
        },
        { field: 'statut', headerName: 'Statut', sortable: true, filter: true },
        {
            field: 'limiteMensuelEs',
            headerName: 'Lim. mens. ES',
            sortable: true,
            valueFormatter: (params) => (params.value ? `${params.value.toFixed(2).replace('.', ',')} L` : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'limiteMensuelGo',
            headerName: 'Lim. mens. GO',
            sortable: true,
            valueFormatter: (params) => (params.value ? `${params.value.toFixed(2).replace('.', ',')} L` : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'limitePrise',
            headerName: 'Limite prise',
            sortable: true,
            valueFormatter: (params) => (params.value ? `${params.value.toFixed(2).replace('.', ',')} L` : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'noLimitUpToMonthLimit',
            headerName: 'Livraison sans recharge',
            cellRenderer: (params: any) => (
                <div style={{ textAlign: 'center' }}>
                    <input
                        type="checkbox"
                        checked={params.value}
                        onChange={(e) => {
                            params.node.setDataValue('isActive', e.target.checked);
                        }}
                    />
                </div>
            ),
            cellStyle: { textAlign: 'center' },
        },

        { field: 'operation', headerName: 'Opération', sortable: true, filter: true, hide: true },
        {
            field: 'modifiedDate',
            headerName: 'Date Modification',
            sortable: true,
            valueFormatter: (params) => (params.value ? format(params.value, 'dd/MM/yyyy hh:mm:ss') : ''),
            filter: 'agDateColumnFilter',
            hide: true,
        },
    ]);

    const myTheme = themeBalham.withParams({ columnBorder: { style: 'dashed', width: '1px' } });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const keys = visibleCols.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        gridRef.current.api.exportDataAsCsv({
            fileName: `${auditView ? 'logs_carte' : 'cartes'}_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys: keys,
            suppressQuotes: false,
        });
    }, [auditView]);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const data: Carte[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => data.push(node.data));
        if (data.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }
        const exportData = data.map((row) => {
            const obj: Record<string, any> = {};
            visibleCols.forEach((col) => {
                const def = col.getColDef();
                const field = def.field;
                const header = def.headerName ?? field ?? '';
                if (!field) return;
                let value = (row as any)[field];
                if (def.filter === 'agDateColumnFilter') {
                    value = value ? format(value, 'dd/MM/yyyy') : '';
                }
                obj[header] = value;
            });
            return obj;
        });
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Cartes');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(wb, `${auditView ? 'logs_carte' : 'cartes'}_export_${timestamp}.xlsx`);
    }, [auditView]);

    useEffect(() => {
        setColumnDefs((prev) =>
            prev.map((col) => {
                if (['operation', 'modifiedDate'].includes(col.field ?? '')) {
                    return { ...col, hide: !(auditView ?? false) };
                }
                if (['designationArticle', 'solde', 'numero', 'type', 'description', 'nomBranche'].includes(col.field ?? '')) {
                    return { ...col, hide: auditView ?? false };
                }
                return col;
            }),
        );
    }, [auditView]);

    return (
        <>
            <div className="" style={{ height: !auditView ? '100vh' : 500, width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact ref={gridRef} theme={myTheme} getRowId={(params) => params.data.id.toString()} rowData={cartes} columnDefs={columnDefs} pagination domLayout="normal" defaultColDef={{ resizable: true, editable: false, minWidth: 100, flex: 1 }} onGridReady={(params) => params.api.autoSizeAllColumns()} suppressExcelExport enableCellTextSelection ensureDomOrder animateRows overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>" />
                </div>
            </div>
            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default CarteGrid;
